import { Alert, Button, Input, LoadingState } from '@orbind/ui'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { OrbindApiError } from '@orbind/api-client'
import { api, webTokenStorage } from '../lib/api.ts'
import { OrbindLogo } from '../shell/OrbindLogo.tsx'
import './convite.css'

/**
 * Aceitar o convite (SPEC-20261001-1035).
 *
 * É a porta de entrada de quem pediu acesso e foi liberado. Três coisas que a
 * tela faz de propósito:
 *
 * - **confere o convite ANTES de desenhar o formulário.** Dizer "este convite
 *   expirou" depois de a pessoa preencher senha e nome do workspace seria
 *   desperdiçar o trabalho dela;
 * - **o e-mail aparece travado.** Ele é o convite — deixar editar faria a
 *   pessoa achar que pode entrar com outro, e aí nada funcionaria;
 * - **entra direto ao terminar.** O clique no link já provou o e-mail; pedir
 *   uma confirmação depois disso seria cerimônia que só atrasa.
 */

type Convite = {
  fullName: string
  email: string
  companyName: string
  plano: { id: string; name: string } | null
  /**
   * O que será cobrado antes de entrar (SPEC-20261001-1516), ou `null` quando
   * não há nada a cobrar. Quem decide é o servidor, a partir do plano do
   * pedido — a tela só desenha.
   */
  cobranca: { cycle: string; seats: number; valorCents: number; temDocumento: boolean } | null
}

const SENHA_MINIMA = 8

const reais = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/** Só os dígitos — é o que o servidor espera e o que valida o tamanho. */
const digitos = (v: string) => v.replace(/\D/g, '')

export function AceitarConvitePage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [convite, setConvite] = useState<Convite | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [invalido, setInvalido] = useState<string | null>(null)

  const [nome, setNome] = useState('')
  const [senha, setSenha] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // cartão (SPEC-20261001-1516) — só usados quando o plano do pedido é pago
  const [numero, setNumero] = useState('')
  const [titular, setTitular] = useState('')
  const [validade, setValidade] = useState('')
  const [cvv, setCvv] = useState('')
  const [documento, setDocumento] = useState('')

  useEffect(() => {
    if (token === '') {
      setInvalido('O endereço não traz nenhum convite.')
      setCarregando(false)
      return
    }
    let vivo = true
    void (async () => {
      try {
        const r = (await (await api.onboarding.invite.$get({ query: { token } })).json()) as {
          convite: Convite
        }
        if (!vivo) return
        setConvite(r.convite)
        setNome(r.convite.fullName)
        // sugestão, não imposição: o nome da empresa costuma ser o do espaço,
        // e quem discordar troca numa tecla
        setWorkspace(r.convite.companyName)
      } catch (e) {
        if (vivo) setInvalido(e instanceof OrbindApiError ? e.message : 'Não foi possível abrir este convite.')
      } finally {
        if (vivo) setCarregando(false)
      }
    })()
    return () => {
      vivo = false
    }
  }, [token])

  /*
   * Plano pago cobra antes de entrar. O cartão só é pedido quando o servidor
   * disse que há o que cobrar: quem foi liberado no grátis não vê nada disso.
   */
  const cobranca = convite?.cobranca ?? null
  const precisaDocumento = Boolean(cobranca) && !cobranca?.temDocumento
  const cartaoCompleto =
    !cobranca ||
    (digitos(numero).length >= 13 &&
      titular.trim().length >= 2 &&
      /^\d{2}\/\d{2}$/.test(validade) &&
      /^\d{3,4}$/.test(cvv) &&
      (!precisaDocumento || [11, 14].includes(digitos(documento).length)))

  const podeEnviar =
    nome.trim().length >= 2 &&
    senha.length >= SENHA_MINIMA &&
    workspace.trim().length >= 2 &&
    cartaoCompleto

  const enviar = async () => {
    if (!podeEnviar || enviando) return
    setEnviando(true)
    setErro(null)
    try {
      const r = (await (
        await api.onboarding.invite.accept.$post({
          json: {
            token,
            name: nome.trim(),
            password: senha,
            workspaceName: workspace.trim(),
            ...(cobranca
              ? {
                  card: { number: digitos(numero), holder: titular.trim(), expiry: validade, cvv },
                  ...(precisaDocumento ? { document: digitos(documento) } : {}),
                }
              : {}),
          },
        })
      ).json()) as { token: string }
      // já entra: a sessão veio junto com o aceite
      webTokenStorage.set(r.token)
      navigate('/home', { replace: true })
    } catch (e) {
      setErro(
        e instanceof OrbindApiError
          ? e.message
          : 'Falha de comunicação. Verifique sua conexão e tente novamente.',
      )
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="ob-root login">
      <section className="login__brand" aria-hidden>
        <div className="login__logo">
          <OrbindLogo size={44} color="#FFFFFF" halo="var(--ob-primary)" />
        </div>
        <h1 className="login__brand-title">Seu acesso ao Orbind está liberado.</h1>
        <p className="login__brand-text">
          Crie sua senha e dê um nome ao espaço da sua equipe. Em seguida você já entra.
        </p>
      </section>

      <section className="login__panel">
        <div className="login__card cnv">
          {carregando ? (
            <LoadingState variant="detail" label="Abrindo o convite" />
          ) : invalido ? (
            <>
              <h2 className="login__title">Convite indisponível</h2>
              <Alert tone="warning" title="Este convite não vale mais">
                {invalido}
              </Alert>
              <p className="cnv__ajuda">
                Peça um convite novo a quem liberou o seu acesso. Se você já criou a conta, é só{' '}
                <a href="/login">entrar</a>.
              </p>
            </>
          ) : convite ? (
            <>
              <h2 className="login__title">Criar meu acesso</h2>
              <p className="cnv__sub">
                Convite para <strong>{convite.email}</strong>
                {convite.plano ? (
                  <>
                    {' '}
                    · plano <strong>{convite.plano.name}</strong>
                  </>
                ) : null}
              </p>

              {erro ? (
                <Alert tone="danger" title="Não foi possível criar o acesso">
                  {erro}
                </Alert>
              ) : null}

              <label className="cnv__campo">
                <span className="cnv__rotulo">Seu nome *</span>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} />
              </label>

              <label className="cnv__campo">
                <span className="cnv__rotulo">E-mail</span>
                {/* travado: é o endereço que recebeu o convite */}
                <Input value={convite.email} readOnly disabled />
                <span className="cnv__dica">É o e-mail do convite e não pode ser trocado aqui.</span>
              </label>

              <label className="cnv__campo">
                <span className="cnv__rotulo">Senha *</span>
                <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} />
                <span className="cnv__dica">Ao menos {SENHA_MINIMA} caracteres.</span>
              </label>

              <label className="cnv__campo">
                <span className="cnv__rotulo">Nome do espaço da equipe *</span>
                <Input value={workspace} onChange={(e) => setWorkspace(e.target.value)} />
                <span className="cnv__dica">Pode mudar depois, nas configurações.</span>
              </label>

              {cobranca ? (
                <>
                  <div className="cnv__cobranca">
                    <h2 className="cnv__cobranca-titulo">Pagamento</h2>
                    <p className="cnv__cobranca-valor">
                      <b>{reais(cobranca.valorCents)}</b>{' '}
                      <span>
                        {cobranca.cycle === 'ANNUAL' ? 'por 12 meses' : 'por mês'} ·{' '}
                        {cobranca.seats} {cobranca.seats === 1 ? 'pessoa' : 'pessoas'}
                      </span>
                    </p>
                    <p className="cnv__cobranca-nota">
                      É o plano que você escolheu ao pedir acesso. Só cobramos quando o acesso
                      funciona: se o cartão for recusado, nada é criado e este link continua valendo.
                    </p>
                  </div>

                  <label className="cnv__campo">
                    <span className="cnv__rotulo">Número do cartão *</span>
                    <Input
                      value={numero}
                      inputMode="numeric"
                      autoComplete="cc-number"
                      maxLength={23}
                      onChange={(e) => setNumero(e.target.value)}
                    />
                  </label>

                  <label className="cnv__campo">
                    <span className="cnv__rotulo">Nome impresso no cartão *</span>
                    <Input
                      value={titular}
                      autoComplete="cc-name"
                      maxLength={26}
                      onChange={(e) => setTitular(e.target.value)}
                    />
                  </label>

                  <div className="cnv__linha">
                    <label className="cnv__campo">
                      <span className="cnv__rotulo">Validade *</span>
                      <Input
                        value={validade}
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        placeholder="MM/AA"
                        maxLength={5}
                        onChange={(e) => setValidade(e.target.value)}
                      />
                    </label>
                    <label className="cnv__campo">
                      <span className="cnv__rotulo">Código de segurança *</span>
                      <Input
                        value={cvv}
                        inputMode="numeric"
                        autoComplete="cc-csc"
                        maxLength={4}
                        onChange={(e) => setCvv(e.target.value)}
                      />
                    </label>
                  </div>

                  {precisaDocumento ? (
                    <label className="cnv__campo">
                      <span className="cnv__rotulo">CPF ou CNPJ de quem paga *</span>
                      <Input
                        value={documento}
                        inputMode="numeric"
                        maxLength={18}
                        onChange={(e) => setDocumento(e.target.value)}
                      />
                    </label>
                  ) : null}
                </>
              ) : null}

              <Button variant="primary" disabled={!podeEnviar || enviando} onClick={() => void enviar()}>
                {enviando ? (cobranca ? 'Cobrando…' : 'Criando…') : cobranca ? 'Pagar e entrar' : 'Criar e entrar'}
              </Button>
            </>
          ) : null}
        </div>
      </section>
    </div>
  )
}
